/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { connect } from 'react-redux'

import type { BillingSubscriptionLevel } from '@modules/ui-types'
import Feature from '@modules/utils/feature'

import { fetchInstanceConfigurationsIfNeeded } from '@/actions/topology/instanceConfigurations'
import { getBasePrices } from '@/apps/userconsole/reducers/pricing/basePricing'
import { getProfile } from '@/apps/userconsole/reducers/profile'
import { fetchBasePricesRequest } from '@/apps/userconsole/reducers/registry'
import { getOrganizationId } from '@/lib/stackDeployments/selectors/metadata'
import { getInstanceConfigurations } from '@/reducers/instanceConfigurations'
import { getOrganizationConfiguration } from '@/reducers/organizations'

import { fetchBasePricesIfNeeded } from '../../../../apps/userconsole/actions/pricing'
import { getConfigForKey, isFeatureActivated } from '../../../../selectors'

import DeploymentTemplateFlyout from './DeploymentTemplateFlyout'

import type { ReduxState, ThunkDispatch } from '../../../../types/redux'
import type { ConsumerProps, DispatchProps, StateProps } from './types'

const mapStateToProps = (
  state: ReduxState,
  { regionId, deployment }: ConsumerProps,
): StateProps => {
  const profile = getProfile(state)!
  const isUserConsole = getConfigForKey(state, `APP_NAME`) === `userconsole`
  const level = isUserConsole ? profile.level : null
  const basePrices = level ? getBasePrices(state, regionId, level) : undefined

  // Get instance configurations for the region (for tier IC customization)
  const regionInstanceConfigurations = getInstanceConfigurations(state, regionId)

  // Get organization configuration to check for legacy IC allowlist
  const organizationId = getOrganizationId({ deployment })
  const organizationConfig = organizationId
    ? getOrganizationConfiguration(state, organizationId)
    : undefined
  const isAllowedToUseDeprecatedResources =
    organizationConfig?.service_restrictions?.can_use_deprecated_resources || false

  return {
    basePrices,
    fetchBasePricesRequest: fetchBasePricesRequest(state, regionId, level),
    profile,
    isUserConsole,
    regionInstanceConfigurations,
    isAllowedToUseDeprecatedResources,
    enableTierIcCustomization: isFeatureActivated(state, Feature.tierIcCustomizationInTemplate),
  }
}

const mapDispatchToProps = (dispatch: ThunkDispatch, { regionId }): DispatchProps => ({
  fetchBasePrices: ({ level }: { regionId: string; level: BillingSubscriptionLevel }) =>
    dispatch(fetchBasePricesIfNeeded({ regionId, level })),
  fetchInstanceConfigurations: () => dispatch(fetchInstanceConfigurationsIfNeeded(regionId)),
})

export default connect<StateProps, DispatchProps, ConsumerProps>(
  mapStateToProps,
  mapDispatchToProps,
)(DeploymentTemplateFlyout)
